// Recharge-cashback engine API (maintenance). Base = /api/maintenance/cashback.
import axiosInstance from "../axiosInstance";

const BASE = "/api/maintenance/cashback";

export interface CashbackTier {
  id?: number;
  program_id?: number;
  name: string;
  min_amount: number;
  max_amount: number | null;
  is_random: boolean;
  min_percentage: number | null;
  max_percentage: number | null;
  fixed_percentage: number | null;
  priority: number;
  is_active: boolean;
}

export interface CashbackProgram {
  id: number;
  name: string;
  is_active: boolean;
  min_payment_amount: number;
  lookback_days: number;
  max_cashback_cap: number | null;
  per_user_monthly_cap: number | null;
  approval_mode: string;
  audit_email: string | null;
  updated_by: string | null;
  updated_at: string | null;
  // campaign settings (migration 030)
  tz?: string | null;
  min_lead_hours?: number | null;
  waiver_approver_emails?: string | null;
  announce_quiet_start?: string | null;
  announce_quiet_end?: string | null;
}

export interface CashbackConfig {
  program: CashbackProgram;
  tiers: CashbackTier[];
}

export interface ScanRow {
  payment_order_id: number;
  user_name: string | null;
  payment_amount: number;
  tier_name: string;
  percentage: number;
  cashback_amount: number;
  status: string;
}

export interface ScanSummary {
  mode: string;
  program_active: boolean;
  approval_mode: string;
  eligible_count: number;
  created: number;
  credited_amount: number;
  previewed: number;
  skipped_no_wallet: number;
  skipped_no_tier: number;
  skipped_capped: number;
  skipped_exists: number;
  per_tier: Record<string, number>;
  rows: ScanRow[];
}

export interface CashbackRow {
  id: number;
  guid: string;
  original_payment_order_id: number;
  transaction_state: string;
  payment_amount: number | null;
  payment_created_at: string | null;
  user_name: string | null;
  organization: string | null;
  cashback_amount: number | null;
  percentage: number | null;
  tier_name: string | null;
}

export interface StateBucket { count: number; amount: number; }
export interface CashbackSummary {
  by_state: Record<string, StateBucket>;
  settled_this_month_amount: number;
  settled_this_month_count: number;
}

export type CashbackProgramUpdate = Partial<Pick<CashbackProgram,
  "is_active" | "min_payment_amount" | "lookback_days" | "max_cashback_cap" | "per_user_monthly_cap" | "approval_mode" | "audit_email"
  | "tz" | "min_lead_hours" | "waiver_approver_emails" | "announce_quiet_start" | "announce_quiet_end">>;

export const cashbackApi = {
  getConfig: () => axiosInstance.get<CashbackConfig>(`${BASE}/config`),
  updateConfig: (data: CashbackProgramUpdate) => axiosInstance.put<CashbackProgram>(`${BASE}/config`, data),
  upsertTier: (t: CashbackTier) => axiosInstance.post<{ id: number }>(`${BASE}/tiers`, t),
  deleteTier: (id: number) => axiosInstance.delete(`${BASE}/tiers/${id}`),
  scan: (dryRun: boolean) => axiosInstance.post<ScanSummary>(`${BASE}/scan`, null, { params: { dry_run: dryRun }, timeout: 60000 }),
  list: (params?: Record<string, any>) => axiosInstance.get<CashbackRow[]>(BASE, { params }),
  summary: () => axiosInstance.get<CashbackSummary>(`${BASE}/summary`),
  approve: (id: number) => axiosInstance.post(`${BASE}/${id}/approve`),
  reject: (id: number) => axiosInstance.post(`${BASE}/${id}/reject`),
};
